import React, { useEffect, useState, useCallback } from 'react';
import { useAmlStore } from '../stores/amlStore';
import { useUIStore } from '../../../stores/uiStore';
import { usePermissions } from '../../../hooks/usePermissions';
import { Permissions } from '../../../config/permissions';
import { SlidePanel, ConfirmationDialog } from '../../../components/ui';
import { useToast } from '../../../components/feedback';
import { getTranslation } from '../../../i18n';
import CustomerList from './customers/CustomerList';
import type { CustomerAction } from './customers/CustomerList';
import CustomerDetailView from './customers/CustomerDetailView';
import CustomerKycView from './customers/CustomerKycView';
import CustomerForm from './customers/CustomerForm';
import TransactionList from './transactions/TransactionList';
import TransactionReportView from './transactions/TransactionReportView';
import TransactionPivotReport from './transactions/TransactionPivotReport';
import SanctionsView from './sanctions/SanctionsView';
import ClientList from './clients/ClientList';
import type { AmlCustomer } from '../types/aml';

type PanelMode = 'detail' | 'kyc' | null;

const AmlView: React.FC = () => {
  const {
    selectedCustomer, showForm, setShowForm, fetchMe,
    selectCustomer, deleteCustomer,
  } = useAmlStore();
  const { amlSection, language } = useUIStore();
  const { hasPermission } = usePermissions();
  const { success, error: toastError } = useToast();
  const t = (key: string, vars?: Record<string, string>) => {
    let str = getTranslation(language, key);
    if (vars) {
      Object.entries(vars).forEach(([k, v]) => { str = str.replace(`{{${k}}}`, v); });
    }
    return str;
  };

  const [panelMode, setPanelMode] = useState<PanelMode>(null);
  const [panelCustomer, setPanelCustomer] = useState<AmlCustomer | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AmlCustomer | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { fetchMe(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (panelMode && panelCustomer && selectedCustomer && selectedCustomer.id === panelCustomer.id) {
      setPanelCustomer(selectedCustomer);
    }
  }, [selectedCustomer]); // eslint-disable-line react-hooks/exhaustive-deps

  const openPanel = useCallback((mode: PanelMode, customer: AmlCustomer) => {
    selectCustomer(customer);
    setPanelCustomer(customer);
    setPanelMode(mode);
  }, [selectCustomer]);

  const closePanel = useCallback(() => {
    setPanelMode(null);
    setPanelCustomer(null);
  }, []);

  const handleAction = useCallback((action: CustomerAction, customer: AmlCustomer) => {
    switch (action) {
      case 'detail':
        openPanel('detail', customer);
        break;
      case 'kyc':
        openPanel('kyc', customer);
        break;
      case 'edit':
        selectCustomer(customer);
        setShowForm('customer-edit');
        break;
      case 'delete':
        setDeleteTarget(customer);
        setShowDeleteConfirm(true);
        break;
    }
  }, [openPanel, selectCustomer, setShowForm]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    const targetName = deleteTarget.name;
    try {
      await deleteCustomer(deleteTarget.id);
      setShowDeleteConfirm(false);
      setDeleteTarget(null);
      if (panelCustomer?.id === deleteTarget.id) {
        closePanel();
      }
      success(t('amlCustomerToastDeleted', { name: targetName }));
    } catch (err: any) {
      const msg = err?.response?.data?.detail || t('amlCustomerToastFailedToDelete');
      toastError(typeof msg === 'string' ? msg : t('amlCustomerToastFailedToDelete'));
    } finally {
      setDeleting(false);
    }
  };

  const panelTitle = panelMode === 'kyc' ? t('amlCustomerPanelTitleKyc') : t('amlCustomerPanelTitleDetail');

  return (
    <div className="h-full flex flex-col min-h-0">
      <div className="flex-1 overflow-auto min-w-0">
        {amlSection === 'customers' && hasPermission(Permissions.AML_CUSTOMERS_VIEW) && (
          <CustomerList onAction={handleAction} />
        )}

        {amlSection === 'transactions' && hasPermission(Permissions.AML_TRANSACTIONS_VIEW) && (
          <TransactionList />
        )}

        {amlSection === 'reports' && hasPermission(Permissions.AML_REPORTS_VIEW) && (
          <TransactionReportView />
        )}

        {amlSection === 'pivot-report' && hasPermission(Permissions.AML_REPORTS_VIEW) && (
          <TransactionPivotReport />
        )}

        {amlSection === 'sanctions' && hasPermission(Permissions.AML_SANCTIONS_VIEW) && (
          <SanctionsView />
        )}

        {amlSection === 'clients' && hasPermission(Permissions.AML_CLIENTS_MANAGE) && (
          <ClientList />
        )}

      </div>

      {/* Detail / KYC SlidePanel */}
      <SlidePanel
        isOpen={!!panelMode && !!panelCustomer}
        onClose={closePanel}
        title={panelTitle}
        width={panelMode === 'kyc' ? 'max-w-[960px]' : 'max-w-[720px]'}
      >
        <div className="p-6">
          {panelMode === 'detail' && panelCustomer && (
            <CustomerDetailView customer={panelCustomer} />
          )}
          {panelMode === 'kyc' && panelCustomer && (
            <CustomerKycView customer={panelCustomer} />
          )}
        </div>
      </SlidePanel>

      {/* Create / Edit SlidePanel */}
      {showForm === 'customer-create' && (
        <CustomerForm customer={null} onClose={() => setShowForm(null)} />
      )}
      {showForm === 'customer-edit' && selectedCustomer && (
        <CustomerForm customer={selectedCustomer} onClose={() => setShowForm(null)} />
      )}

      {/* Delete Confirmation */}
      <ConfirmationDialog
        isOpen={showDeleteConfirm}
        onClose={() => { setShowDeleteConfirm(false); setDeleteTarget(null); }}
        onConfirm={handleDelete}
        title={t('amlCustomerDeleteTitle')}
        message={t('amlCustomerDeleteMessage', { name: deleteTarget?.name || '' })}
        confirmText={t('amlCustomerDeleteConfirm')}
        cancelText={t('amlCustomerDeleteCancel')}
        variant="danger"
      />
    </div>
  );
};

export default AmlView;
